import { useCallback, useId, useState, type MouseEvent, type ReactNode } from 'react'
import { workspaceListRowChrome } from '@/components/features/inbox/workspace-accent'
import { WorkspaceDot } from '@/components/layout/workspace-dot'
import { WorkspaceTagStack } from '@/components/layout/workspace-tag-stack'
import type { WorkspaceTagDot } from '@/lib/workspace-tag-stack'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { closeTransientMenus } from '@/lib/transient-menus'
import { cn } from '@/lib/utils'

export type SidebarRowState = {
  /** Item is open in a tab or surface. */
  open?: boolean
  /** Keyboard or hover focus within the list. */
  focused?: boolean
  /** Primary selection (e.g. active document / thread). */
  active?: boolean
}

export type SidebarListRowProps = {
  state?: SidebarRowState
  workspaceColor?: string | null
  workspaceName?: string
  /** Front-first tagged workspaces. When 2+, render overlapping dots instead of a single pip. */
  workspaceTags?: WorkspaceTagDot[]
  dense?: boolean
  /** When false, row keeps workspace border chrome but omits the hover dot (e.g. inbox rows already show workspace in metadata). */
  showWorkspaceDot?: boolean
  onActivate?: (event: MouseEvent<HTMLDivElement>) => void
  onDoubleClick?: (event: MouseEvent<HTMLDivElement>) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  contextMenuEntries?: EntityContextMenuEntry[]
  contextMenuTestId?: string
  /** Legacy / custom menus (e.g. flows ContextMenu) when entries are not used. */
  onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void
  hoverActions?: ReactNode
  workspaceTagStackTestId?: string
  children: ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * Flat sidebar list row: workspace dot, 2px left accent, open/focused/active states.
 * Forwards click events for resolveOpenIntent; right-click uses EntityContextMenu.
 */
export function SidebarListRow({
  state = {},
  workspaceColor,
  workspaceName,
  workspaceTags,
  dense = false,
  showWorkspaceDot = true,
  onActivate,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  contextMenuEntries,
  contextMenuTestId,
  onContextMenu: onContextMenuProp,
  hoverActions,
  workspaceTagStackTestId,
  children,
  className,
  'data-testid': testId,
}: SidebarListRowProps) {
  const { open = false, focused = false, active = false } = state
  const rowChrome = workspaceListRowChrome(workspaceColor ?? undefined, active || (open && focused))
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const menuSourceId = useId()
  const stackedTags = (workspaceTags ?? []).filter((t) => t.color)
  const showTagStack = showWorkspaceDot && stackedTags.length > 1
  const showSingleDot = showWorkspaceDot && Boolean(workspaceColor) && !showTagStack
  const leadingPad = showTagStack
    ? stackedTags.length >= 3
      ? 'pl-5'
      : 'pl-4'
    : showSingleDot
      ? 'pl-3'
      : undefined

  const handleContextMenu = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (onContextMenuProp) {
        e.preventDefault()
        e.stopPropagation()
        // Close overflow dropdowns before a custom handler opens its own menu.
        closeTransientMenus(menuSourceId)
        onContextMenuProp(e)
        return
      }
      if (!contextMenuEntries?.length) return
      e.preventDefault()
      e.stopPropagation()
      // Dismiss any open `...` dropdown (or other row context menu) first.
      closeTransientMenus(menuSourceId)
      setMenu({ x: e.clientX, y: e.clientY })
    },
    [contextMenuEntries?.length, menuSourceId, onContextMenuProp],
  )

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={onActivate}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onActivate?.(e as unknown as MouseEvent<HTMLDivElement>)
          }
        }}
        className={cn(
          'group/row relative w-full text-left border-b border-border border-l-2 border-l-transparent cursor-pointer transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          dense ? 'px-3 py-1.5' : 'px-3 py-2.5',
          focused && 'ring-1 ring-primary/30 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.12)]',
          open && !focused && !active && 'bg-primary/[0.04]',
          // Match HomeFeedRow / ExtensionListRow: accent wash + 2px primary rail.
          // Do not use border-l-primary (it recolors border-b into a box).
          active &&
            'bg-accent hover:bg-accent before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary',
          !active && rowChrome.className,
          className,
        )}
        style={active ? undefined : rowChrome.style}
        data-testid={testId}
        data-open={open ? 'true' : undefined}
        data-focused={focused ? 'true' : undefined}
        data-active={active ? 'true' : undefined}
        data-selected={active ? 'true' : undefined}
        aria-selected={active || undefined}
        aria-current={focused ? 'true' : undefined}
      >
        {showTagStack ? (
          <WorkspaceTagStack
            tags={stackedTags}
            className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/row:opacity-100 sm:opacity-100"
            data-testid={workspaceTagStackTestId}
          />
        ) : showSingleDot ? (
          <WorkspaceDot
            color={workspaceColor}
            active={active || open}
            size="sm"
            className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/row:opacity-100 sm:opacity-100"
            title={workspaceName}
          />
        ) : null}
        <div className={cn(leadingPad)}>{children}</div>
        {hoverActions ? (
          <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover/row:opacity-100 flex items-center gap-0.5 rounded-md bg-accent/95 pl-2 pr-0.5">
            {hoverActions}
          </div>
        ) : null}
      </div>
      {menu && contextMenuEntries?.length ? (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          entries={contextMenuEntries}
          menuSourceId={menuSourceId}
          data-testid={contextMenuTestId}
        />
      ) : null}
    </>
  )
}
